import { useMutation } from '@apollo/client';
import {
  ALL_MODULES,
  EventEmitter,
  getConversationOrder,
  getCurrentUserHook,
  toast,
  useActivityTracker,
  withErrorBoundary
} from '@nile/components';
import { useSuperEditor } from '@nile/components/src/Components/RichTextEditor/useSuperEditor';
import { ADD_NOTE_WORKITEM } from '@nile/graphql';
import { useTicketConversationActionStore } from '@nile/mangifera/src/StoreManager/store/TicketDetails/TicketConversationActionStore';
import { SVGIcon } from '@superops/super-icons';
import { Button } from '@superops/ui-design-system';
import React from 'react';
import { FormattedMessage } from 'react-intl';

const SendTicketNoteAction = (props) => {
  const { data } = props;

  const conversationOrder = getConversationOrder() || 'ASC';

  const editorConfig = data?.config?.editor?.storeConfig;
  const ticket = data?.data?.ticket;
  const ticketId = ticket?.id;
  const currentUser = getCurrentUserHook();

  const { template, resetTemplate } = useSuperEditor(editorConfig);

  const ticketConversationStore = useTicketConversationActionStore({
    ticketId: ticketId
  });

  const isPublicNote = ticketConversationStore(
    (state) => state?.notes?.isPublicNote
  );

  const resetNotesData = ticketConversationStore(
    (state) => state?.resetNotesData
  );

  const resetData = () => {
    resetTemplate();
    resetNotesData();
  };

  const [sendTicketNote, { loading: sendNoteLoading }] = useMutation(
    ADD_NOTE_WORKITEM,
    {
      update(cache, { data: mutationData }) {
        const addedNote = mutationData?.createNote;
        if (!addedNote) return;

        const newFeed = {
          __typename: 'TicketAudit',
          id: addedNote.noteId,
          type: 'NOTE',
          time: addedNote.addedOn,

          user: {
            userId: currentUser?.userId,
            name: currentUser?.name,
            firstName: currentUser?.firstName,
            lastName: currentUser?.lastName,
            email: currentUser?.email
          },

          conversation: null,
          threadConversation: null,
          conversationThread: null,
          vendorConversation: null,

          note: {
            __typename: 'Note',
            noteId: addedNote.noteId,
            content: addedNote.content,
            addedOn: addedNote.addedOn,
            addedBy: addedNote.addedBy,
            attachments: addedNote.attachments ?? [],
            privacyType: addedNote.privacyType,
            workItem: addedNote.workItem
          },

          approval: null,
          task: null
        };

        cache.modify({
          fields: {
            getTicketAuditList(existing = {}, { storeFieldName, readField }) {
              const match = storeFieldName.match(/\((.*)\)$/);
              if (!match) return existing;

              let parsedArgs;
              try {
                parsedArgs = JSON.parse(match[1]);
              } catch {
                return existing;
              }

              const cacheTicketId = parsedArgs?.input?.ticket?.ticketId;

              if (cacheTicketId !== ticketId) {
                return existing;
              }

              const existingFeeds = existing.ticketAudit ?? [];

              const alreadyExists = existingFeeds.some(
                (ref) => readField('id', ref) === newFeed.id
              );
              if (alreadyExists) return existing;

              return {
                ...existing,
                ticketAudit:
                  conversationOrder === 'DESC'
                    ? [newFeed, ...existingFeeds]
                    : [...existingFeeds, newFeed]
              };
            }
          }
        });
      },

      onCompleted() {
        toast('success', 'New note created successfully');
        useActivityTracker({ event: 'Ticket - Added internal note' });
        EventEmitter.dispatch('focusLastAddedMessage');
        EventEmitter.dispatch('closeGlobalConversation', ticketId);
        resetData();
      }
    }
  );

  const formatAttachments = (attachments) => {
    return attachments?.map((attachment) => {
      return {
        fileName: attachment?.fileName,
        originalFileName: attachment?.originalFileName,
        fileSize: attachment?.fileSize
      };
    });
  };

  const getNotesVariables = () => {
    return {
      input: {
        content: template?.content,
        mentions: template?.mentions,
        workItem: {
          workId: ticket?.ticketId,
          module: ALL_MODULES.ticket
        },
        addAttachments: formatAttachments(template?.attachments),
        privacyType: isPublicNote ? 'PUBLIC' : 'PRIVATE',
        inlineImages: template?.inlineImages || []
      }
    };
  };

  const sendNote = () => {
    const variables = getNotesVariables();
    try {
      sendTicketNote({
        variables: variables
      });
    } catch (error) {
      console.log(error);
    }
  };

  const isDisabled = !template;

  return (
    <div>
      <Button
        icon={<SVGIcon name="SendWhite" color="#fff" />}
        primary
        loading={sendNoteLoading}
        onClick={sendNote}
        disabled={isDisabled}
      >
        <FormattedMessage id="common.send" />
      </Button>
    </div>
  );
};

export default withErrorBoundary(SendTicketNoteAction);
